import IParamScript from '../database/interfaces/paramScript/IParamScript';

/**
 * Shared helpers for admin parameter editing — kept here so value (de)serialization is consistent
 * across StyleParamsEditor and DescriptorEditorDialog (they previously diverged).
 */

/** True for non-null objects/arrays (Decor-style values), false for scalars/null. */
export const isObjectValue = (v: unknown): boolean => v !== null && typeof v === 'object';

/** Serialize a parameter value to editable text: objects → pretty JSON, scalars → string. */
export const valueToText = (value: any): string =>
    isObjectValue(value) ? JSON.stringify(value, null, 2) : (value == null ? '' : String(value));

/**
 * Parse edited text back to a value, matching the original's shape:
 * object-typed → JSON.parse; numeric/boolean scalars → coerced to that primitive; else string.
 * Throws on invalid JSON for object-typed values (callers validate before calling).
 */
export const textToValue = (original: any, text: string): any => {
    if (isObjectValue(original)) return JSON.parse(text);
    if (typeof original === 'number') {
        const n = Number(text);
        return Number.isNaN(n) ? text : n;
    }
    if (typeof original === 'boolean') return text.trim().toLowerCase() === 'true';
    return text;
};

/** Serialize a value to the canonical string stored in uc_style_parameter_values (varchar). */
export const valueToStored = (value: any): string =>
    isObjectValue(value) ? JSON.stringify(value) : (value == null ? '' : String(value));

/** Human-readable identity line: `name.property[idx] · targetPath`. */
export const formatParamRef = (p: IParamScript): string => {
    const idx = p.parameterPropertyIndex != null ? `[${p.parameterPropertyIndex}]` : '';
    const path = p.targetPath ? ` · ${p.targetPath}` : '';
    return `${p.parameterName}.${p.parameterProperty}${idx}${path}`;
};

/** Face order of the 6-value `Margin` string (confirmed against Unity 2026-06-09). Single source of truth. */
export const MARGIN_LABELS = ['Bal', 'Alsó', 'Elülső', 'Jobb', 'Felső', 'Hátsó'];

/** Render a 6-value margin string ("0,0,30,0,0,0") as labelled, human-readable text. */
export const formatMargin = (value: string): string => {
    const parts = (value ?? '').split(',');
    return MARGIN_LABELS.map((lbl, i) => `${lbl}: ${((parts[i] ?? '').trim()) || '0'}`).join(', ');
};
